'use client';

import type { DialogProps } from '@mui/material';
import {
  Box,
  Dialog,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { useMemo } from 'react';
import { TixlyIconButton } from './button/tixly-icon-button';
import { FeatureIcon } from './feature-icon';

type SwipableDialogProps = {
  onClose?(): void;
  onClosed?(): void;
  /**
   * If true, forces the dialog to appear as a SwipeableDrawer.
   * If false, forces the dialog to appear as a standard Dialog.
   * If undefined, it will be responsive (Drawer on mobile, Dialog on desktop).
   */
  swipable?: boolean;
  headerText?: string;
  subtitleText?: string;
  headerIcon?: React.ReactNode;
  primaryActionButton?: React.ReactNode;
  secondaryActionButton?: React.ReactNode;
  hideCloseButton?: boolean;
  /**
   * xs: '360px'
   * sm: '480px'
   * md: '600px'
   * lg: '660px'
   */
  maxWidth?: DialogProps['maxWidth'];
} & DialogProps;

// Swipable bottom sheet on mobile. Modal in desktop.
export function SwipableDialog({
  children,
  open,
  onClose,
  onClosed,
  swipable,
  headerText,
  subtitleText,
  headerIcon,
  primaryActionButton,
  secondaryActionButton,
  hideCloseButton = false,
  maxWidth = 'md',
  ...rest
}: SwipableDialogProps) {
  const theme = useTheme();
  const closeIcon = useMemo(
    () => (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          right: 0,
          zIndex: 1,
          height: 0,
        }}
      >
        <TixlyIconButton
          color="monochrome"
          variant="contained"
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'action.active',
            backgroundColor: 'action.disabledBackground',
            '&:hover': {
              color: 'action.active',
            },
          }}
          onClick={onClose}
        >
          <CrossIcon />
        </TixlyIconButton>
      </Box>
    ),
    [onClose]
  );

  const swipeableBreakpoint = useMediaQuery(theme.breakpoints.down('sm'));

  const dialog = (
    <Dialog
      slotProps={{
        backdrop: {
          onExited: () => onClosed?.(),
        },
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: {
            sm: 'calc(100vh - 60px)',
            md: 'calc(100vh - 80px)',
            lg: 'calc(100vh - 100px)',
            xl: 'calc(100vh - 120px)',
          },
        },
        '& .MuiDialog-paperWidthXs': { maxWidth: '360px', width: '100%' },
        '& .MuiDialog-paperWidthSm': { maxWidth: '480px', width: '100%' },
        '& .MuiDialog-paperWidthMd': { maxWidth: '600px', width: '100%' },
        '& .MuiDialog-paperWidthLg': { maxWidth: '660px', width: '100%' },
      }}
      maxWidth={maxWidth}
      {...rest}
      open={open}
      onClose={onClose}
    >
      {!hideCloseButton && closeIcon}
      <Box sx={{ p: 4, pt: hideCloseButton ? undefined : 6 }}>
        {headerIcon && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 4 }}>
            <FeatureIcon icon={headerIcon} color="primary" size="large" />
          </Box>
        )}
        {headerText && (
          <Typography
            variant="h4"
            align="center"
            // The modal template spaces the icon 24px from the title and the
            // title 16px from the subtitle.
            sx={{ pt: headerIcon ? 0 : 1, pb: subtitleText ? 2 : 4 }}
          >
            {headerText}
          </Typography>
        )}
        {subtitleText && (
          <Typography
            variant="subtitle1"
            component="p"
            align="center"
            sx={{ pb: 4, color: 'text.secondary' }}
          >
            {subtitleText}
          </Typography>
        )}
        {children}
        {(primaryActionButton || secondaryActionButton) && (
          <Stack
            direction="row"
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              // The header already ends on its own 32px padding.
              mt: children ? 3 : 0,
              justifyContent: 'center',
            }}
          >
            {secondaryActionButton}
            {primaryActionButton}
          </Stack>
        )}
      </Box>
    </Dialog>
  );

  if (swipable ?? swipeableBreakpoint) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        sx={{
          '& .MuiDrawer-paper': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
          '& .MuiMenuItem-root': {
            py: 2,
            fontSize: 16,
          },
          '& .MuiMenuItem-root .MuiSvgIcon-root': {
            width: 24,
          },
        }}
        slotProps={{
          backdrop: {
            onExited: () => onClosed?.(),
          },
        }}
        open={open}
        onOpen={() => {
          // done
        }}
        onClose={() => {
          // done
          onClose?.();
        }}
      >
        {!hideCloseButton && closeIcon}
        <Box sx={{ p: 4, pt: hideCloseButton ? undefined : 6 }}>
          {headerIcon && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 3 }}
            >
              <FeatureIcon icon={headerIcon} color="primary" size="large" />
            </Box>
          )}
          {headerText && (
            <Typography variant="h4" align="center" sx={{ py: 1 }}>
              {headerText}
            </Typography>
          )}
          {subtitleText && (
            <Typography
              variant="subtitle1"
              component="p"
              align="center"
              sx={{ pb: 3, color: 'text.secondary' }}
            >
              {subtitleText}
            </Typography>
          )}
          {children}
          {(primaryActionButton || secondaryActionButton) && (
            <Stack
              direction="column"
              sx={{
                gap: 1,
                width: '100%',
                mt: 3,
              }}
            >
              {primaryActionButton}
              {secondaryActionButton}
            </Stack>
          )}
        </Box>
      </SwipeableDrawer>
    );
  }

  return dialog;
}
